import { Accordion, AccordionProps, Button, Group } from '@mantine/core';
import { isEmpty } from 'lodash/fp';
import React from 'react';

import {
  DeviceModelType as OrgDeviceModelType,
  DeviceType,
} from '@portals/api/organizations';
import { DeviceModelType as PartnerDeviceModelType } from '@portals/api/partners';
import { useOpenModal } from '@portals/redux';
import {
  AccessLevelEnum,
  DeviceType as CommonDeviceType,
  SupportedCommandType,
} from '@portals/types';

import { EntityAccess } from '../../../../EntityAccess';

interface CommandsProps<
  TDevice extends DeviceType | CommonDeviceType,
  TDeviceModel extends OrgDeviceModelType | PartnerDeviceModelType
> {
  device: TDevice;
  model: TDeviceModel;
  setIsOpenTunnelCommandSent: (value: boolean) => void;
}

export function Commands<
  TDevice extends DeviceType | CommonDeviceType,
  TDeviceModel extends OrgDeviceModelType | PartnerDeviceModelType
>({
  device,
  model,
  setIsOpenTunnelCommandSent,
}: CommandsProps<TDevice, TDeviceModel>) {
  const openModal = useOpenModal();

  if (isEmpty(model?.supported_commands)) return null;

  const visibleCommands = model?.supported_commands.filter(
    (command) => command.is_visible_in_panel
  );

  if (isEmpty(visibleCommands)) {
    return null;
  }

  const onCommandSend = (command: SupportedCommandType) => {
    openModal('RunCommand', {
      deviceType: device.partner.type_id,
      command,
      deviceIds: [device.id],
      setIsOpenTunnelCommandSent,
    });
  };

  return (
    <Group>
      {visibleCommands.map((command) => (
        <EntityAccess
          key={command.id}
          id={command.id}
          minLevel={AccessLevelEnum.Edit}
          entity={device}
        >
          <Button
            maw="fit-content"
            size="sm"
            variant="light"
            data-testid={`run-command-${command.friendly_name}`}
            onClick={() => onCommandSend(command)}
          >
            {command.friendly_name}
          </Button>
        </EntityAccess>
      ))}
    </Group>
  );
}

const accordionStyles: AccordionProps['styles'] = (theme) => ({
  root: {
    backgroundColor: theme.colors.gray[0],
  },

  item: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: theme.white,
  },

  label: {
    color: theme.colors.gray[7],
    paddingBlock: theme.spacing.xl,
  },

  control: {
    paddingInline: 0,

    '&:hover': {
      backgroundColor: 'inherit',
    },
  },

  content: {
    paddingInline: 0,
    paddingTop: 0,
  },
});
